/**
 * ScrollAnimations Module
 *
 * Reveal-on-scroll animations powered by IntersectionObserver.
 * Supports fade-in / slide-up reveals, staggered children and
 * lightweight parallax transforms.
 *
 * @module ScrollAnimations
 */
export default class ScrollAnimations {

  static DEFAULTS = {
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px',
    staggerDelay: 100,
    selectors: {
      reveal: '[data-animate], .wleb-reveal',
      stagger: '[data-animate-stagger], .wleb-stagger',
      parallax: '[data-parallax]',
    },
    parallaxSpeed: 0.3,
  };

  /**
   * @param {Object} options - Merged with DEFAULTS.
   */
  constructor(options = {}) {
    this.config = { ...ScrollAnimations.DEFAULTS, ...options };

    this._revealElements = document.querySelectorAll(this.config.selectors.reveal);
    this._staggerContainers = document.querySelectorAll(this.config.selectors.stagger);
    this._parallaxElements = document.querySelectorAll(this.config.selectors.parallax);

    this._revealObserver = null;
    this._staggerObserver = null;
    this._parallaxObserver = null;
    this._parallaxTicking = false;
    this._activeParallaxElements = new Set();

    this._init();
  }

  /* ------------------------------------------------------------------
   * Bootstrap
   * ----------------------------------------------------------------*/

  _init() {
    this._initReveal();
    this._initStagger();
    this._initParallax();
  }

  /* ------------------------------------------------------------------
   * Reveal animations (fade-in / slide-up)
   * ----------------------------------------------------------------*/

  _initReveal() {
    if (!this._revealElements.length) {
      return;
    }

    // Set initial hidden state
    this._revealElements.forEach((el) => {
      el.setAttribute('data-state', 'hidden');
    });

    this._revealObserver = new IntersectionObserver(
      this._handleReveal.bind(this),
      {
        threshold: this.config.threshold,
        rootMargin: this.config.rootMargin,
      },
    );

    this._revealElements.forEach((el) => this._revealObserver.observe(el));
  }

  /**
   * Transition elements from hidden to visible once they enter the
   * viewport.  The observer disconnects from each element after it has
   * been revealed so we only animate once.
   */
  _handleReveal(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }

      entry.target.setAttribute('data-state', 'visible');
      this._revealObserver.unobserve(entry.target);
    });
  }

  /* ------------------------------------------------------------------
   * Staggered children
   * ----------------------------------------------------------------*/

  _initStagger() {
    if (!this._staggerContainers.length) {
      return;
    }

    // Hide all direct children up-front
    this._staggerContainers.forEach((container) => {
      const children = this._getStaggerChildren(container);
      children.forEach((child) => {
        child.setAttribute('data-state', 'hidden');
      });
    });

    this._staggerObserver = new IntersectionObserver(
      this._handleStagger.bind(this),
      {
        threshold: this.config.threshold,
        rootMargin: this.config.rootMargin,
      },
    );

    this._staggerContainers.forEach((el) => this._staggerObserver.observe(el));
  }

  /**
   * Return the children of a stagger container. If the container
   * specifies a selector via `data-animate-stagger`, use it; otherwise
   * fall back to direct children.
   */
  _getStaggerChildren(container) {
    const selector = container.getAttribute('data-animate-stagger');
    if (selector && selector !== 'true' && selector !== '') {
      return Array.from(container.querySelectorAll(selector));
    }
    return Array.from(container.children);
  }

  _handleStagger(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        return;
      }

      const children = this._getStaggerChildren(entry.target);

      children.forEach((child, index) => {
        const delay = index * this.config.staggerDelay;

        setTimeout(() => {
          child.setAttribute('data-state', 'visible');
        }, delay);
      });

      this._staggerObserver.unobserve(entry.target);
    });
  }

  /* ------------------------------------------------------------------
   * Parallax
   * ----------------------------------------------------------------*/

  _initParallax() {
    if (!this._parallaxElements.length) {
      return;
    }

    // Observe visibility so we only compute transforms for on-screen
    // elements — keeps the scroll handler cheap.
    this._parallaxObserver = new IntersectionObserver(
      this._handleParallaxVisibility.bind(this),
      { threshold: 0 },
    );

    this._parallaxElements.forEach((el) => {
      this._parallaxObserver.observe(el);
    });

    window.addEventListener('scroll', this._onParallaxScroll.bind(this), { passive: true });
  }

  _handleParallaxVisibility(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this._activeParallaxElements.add(entry.target);
      } else {
        this._activeParallaxElements.delete(entry.target);
      }
    });
  }

  _onParallaxScroll() {
    if (this._parallaxTicking || !this._activeParallaxElements.size) {
      return;
    }

    this._parallaxTicking = true;

    window.requestAnimationFrame(() => {
      this._activeParallaxElements.forEach((el) => {
        this._applyParallax(el);
      });
      this._parallaxTicking = false;
    });
  }

  _applyParallax(el) {
    const speed = parseFloat(el.getAttribute('data-parallax')) || this.config.parallaxSpeed;
    const rect = el.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    // Calculate position relative to viewport centre so the effect
    // is symmetrical whether the element enters from top or bottom.
    const centre = rect.top + rect.height / 2;
    const offset = (centre - windowHeight / 2) * speed;

    el.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
  }

  /* ------------------------------------------------------------------
   * Public API
   * ----------------------------------------------------------------*/

  /** Tear down observers and listeners. */
  destroy() {
    if (this._revealObserver) {
      this._revealObserver.disconnect();
    }
    if (this._staggerObserver) {
      this._staggerObserver.disconnect();
    }
    if (this._parallaxObserver) {
      this._parallaxObserver.disconnect();
    }

    window.removeEventListener('scroll', this._onParallaxScroll);
    this._activeParallaxElements.clear();
  }
}
